import Vue from 'vue'
import Router from 'vue-router'

import Layout from '@/views/Layout'
import ClientLayout from '@/views/ClientLayout'

import {
    isAuth,
    isNotAuth,
    checkClientReferralCode,
    checkBusinessReferralCode,
    checkBusinessConfirmationCode,
    isAccountConfirmed
} from './guard'

Vue.use(Router)

export const constantRouterMap = [
    {
        path: '',
        redirect: '/dashboard',
        component: Layout,
        children: [
            {
                path: 'dashboard',
                name: 'dashboard',
                beforeEnter: isAuth,
                component: () => import('@/views/Dashboard'),
                meta: {
                    permission: ['leader', 'business']
                }
            },
            {
                path: 'referral-codes',
                name: 'referralCodes',
                beforeEnter: isAuth,
                component: () => import('@/views/ReferralCodes'),
                meta: {
                    permission: ['business', 'leader']
                }
            },
            {
                path: 'account-balance-history',
                name: 'accountBalanceHistory',
                beforeEnter: isAuth,
                component: () => import('@/views/AccountBalanceHistories'),
                meta: {
                    permission: ['admin', 'business', 'leader'],
                    accessPermission: 'watch-account-balance-history'
                }
            },
            {
                path: 'client-refill-history',
                name: 'clientRefillHistory',
                beforeEnter: isAuth,
                component: () => import('@/views/ClientRefillHistory'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'watch-client-refill-history'
                }
            },
            {
                path: 'payments',
                name: 'payments',
                beforeEnter: isAuth,
                component: () => import('@/views/Payments'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'watch-payments'
                }
            },
            {
                path: 'payments/:id',
                name: 'paymentLog',
                beforeEnter: isAuth,
                component: () => import('@/views/PaymentLog'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'watch-payments'
                }
            },
            {
                path: '/unallocated-clients',
                name: 'unallocatedClients',
                beforeEnter: isAuth,
                component: () => import('@/views/UnallocatedClients'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'watch-unallocated-clients'
                }
            },
            {
                path: 'referral',
                name: 'referral',
                beforeEnter: isAuth,
                component: () => import('@/views/Referrals'),
                meta: {
                    permission: ['admin', 'business', 'leader'],
                    accessPermission: 'watch-referrals'
                }
            },
            {
                path: 'business',
                name: 'business',
                beforeEnter: isAuth,
                component: () => import('@/views/Businesses'),
                meta: {
                    permission: ['admin', 'leader'],
                    accessPermission: 'watch-business'
                }
            },
            {
                path: 'news',
                name: 'news',
                beforeEnter: isAuth,
                component: () => import('@/views/News'),
                meta: {
                    permission: ['admin', 'leader', 'business'],
                    accessPermission: 'create-news'
                }
            },
            ...['/news/editor', '/news/editor/:id'].map(path => ({
                path,
                beforeEnter: isAuth,
                component: () => import('@/views/NewsEditor'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'create-news'
                }
            })),
            {
                path: 'card',
                name: 'card',
                beforeEnter: isAuth,
                component: () => import('@/views/Cards'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'watch-cards'
                }
            },
            {
                path: 'balance-history',
                name: 'balanceHistory',
                beforeEnter: isAuth,
                component: () => import('@/views/BalanceHistories'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'watch-balance-history'
                }
            },
            {
                path: 'customers',
                name: 'customers',
                beforeEnter: isAuth,
                component: () => import('@/views/Customers'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'client-news'
                }
            },
            {
                path: 'customers/:id',
                name: 'customerEditor',
                beforeEnter: isAuth,
                component: () => import('@/views/CustomerEditor'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'client-news'
                }
            },
            {
                path: 'notification',
                name: 'notification',
                beforeEnter: isAuth,
                component: () => import('@/views/Notification'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'send-push-notifications'
                }
            },
            {
                path: 'add-client-article',
                name: 'addClientArticle',
                beforeEnter: isAuth,
                component: () => import('@/views/AddClientArticle'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'client-news'
                }
            },
            {
                path: 'client-news',
                name: 'clientNews',
                beforeEnter: isAuth,
                component: () => import('@/views/ClientNews'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'client-news'
                }
            },
            {
                path: 'create-account',
                name: 'createAccount',
                beforeEnter: isAuth,
                component: () => import('@/views/CreateAccounts'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'create-account'
                }
            },
            {
                path: 'admins',
                name: 'admins',
                beforeEnter: isAuth,
                component: () => import('@/views/Admins'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'edit-admins'
                }
            },
            {
                path: 'admins/editor/:id',
                name: 'adminEditor',
                beforeEnter: isAuth,
                component: () => import('@/views/AdminEditor'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'edit-admins'
                }
            },
            {
                path: 'client-news/editor/:id',
                name: 'clientArticleEditor',
                beforeEnter: isAuth,
                component: () => import('@/views/ClientArticleEditor'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'client-news'
                }
            },
            {
                path: 'groups',
                name: 'groups',
                beforeEnter: isAuth,
                component: () => import('@/views/Groups'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'watch-client-groups'
                }
            },
            {
                path: 'cashback',
                name: 'cashbackSystems',
                beforeEnter: isAuth,
                component: () => import('@/views/Cashback'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'watch-cashback-system'
                }
            },
            ...[{ path: '/cashback/editor', name: 'cashbackCreator' },
                { path: '/cashback/editor/:id', name: 'cashbackEditor' }
            ].map(({ path, name }) => ({
                path, name,
                beforeEnter: isAuth,
                component: () => import('@/views/CashbackEditor'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'watch-cashback-system'
                }
            })),
            {
                path: 'permissions',
                name: 'permissions',
                beforeEnter: isAuth,
                component: () => import('@/views/Permissions'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'change-admin-permissions'
                }
            },
            {
                path: 'roles',
                name: 'roles',
                beforeEnter: isAuth,
                component: () => import('@/views/RolesManaging/Roles'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'edit-roles'
                }
            },
            ...['/roles/editor', '/roles/editor/:id'].map(path => ({
                path,
                beforeEnter: isAuth,
                component: () => import('@/views/RolesManaging/RoleEditor'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'edit-roles'
                }
            })),
            {
                path: 'leaders',
                name: 'leaders',
                beforeEnter: isAuth,
                component: () => import('@/views/Leaders'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'watch-leaders'
                }
            },
            {
                path: 'payouts',
                name: 'payouts',
                beforeEnter: isAuth,
                component: () => import('@/views/Payouts'),
                meta: {
                    permission: ['admin', 'leader', 'business'],
                    accessPermission: 'edit-withdraws'
                }
            },
            {
                path: 'payouts/:id',
                name: 'payoutsLogs',
                beforeEnter: isAuth,
                component: () => import('@/views/PayoutsLogs'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'edit-withdraws'
                }
            },
            /* Temporary disabled
            {
                path: 'reports',
                beforeEnter: isAuth,
                component: () => import('@/views/Reports'),
                meta: {
                    permission: ['admin', 'leader', 'business']
                },
                children: [
                    {
                        path: 'business/date-grouping',
                        name: 'businessReport',
                        beforeEnter: isAuth,
                        component: () => import('@/views/Reports/Business/DateGrouping'),
                        meta: {
                            permission: ['business'],
                            accessPermission: 'watch-reports'
                        }
                    },
                    {
                        path: 'leader/business-grouping',
                        name: 'leaderReport',
                        beforeEnter: isAuth,
                        component: () => import('@/views/Reports/Leader/BusinessGrouping'),
                        meta: {
                            permission: ['leader'],
                            accessPermission: 'watch-reports'
                        }
                    },
                    {
                        path: 'admin/leader-grouping',
                        name: 'reportLeaderGroup',
                        beforeEnter: isAuth,
                        component: () => import('@/views/Reports/Admin/LeaderGrouping'),
                        meta: {
                            permission: ['admin'],
                            accessPermission: 'watch-reports'
                        }
                    },
                    {
                        path: 'admin/business-grouping',
                        name: 'reportBusinessGroup',
                        beforeEnter: isAuth,
                        component: () => import('@/views/Reports/Admin/BusinessGrouping'),
                        meta: {
                            permission: ['admin'],
                            accessPermission: 'watch-reports'
                        }
                    },
                    {
                        path: 'admin/day-grouping',
                        name: 'reportDayGroup',
                        beforeEnter: isAuth,
                        component: () => import('@/views/Reports/Admin/DayGrouping'),
                        meta: {
                            permission: ['admin'],
                            accessPermission: 'watch-reports'
                        }
                    }
                ]
            }, */
            {
                path: '/extended-reports',
                name: 'reports',
                beforeEnter: isAuth,
                component: () => import('@/views/SqlReports'),
                meta: {
                    permission: ['admin', 'leader'],
                    accessPermission: 'watch-extended-reports'
                }
            },
            {
                path: '/extended-reports/list',
                name: 'extendedReportsList',
                beforeEnter: isAuth,
                component: () => import('@/views/SqlReportsList'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'edit-extended-reports'
                }
            },
            {
                path: '/extended-reports/categories',
                name: 'extendedReportsCategories',
                beforeEnter: isAuth,
                component: () => import('@/views/SqlReportsCategories'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'edit-extended-reports'
                }
            },
            ...[
                '/extended-reports/editor',
                '/extended-reports/editor/:id'
            ].map(path => ({
                path,
                beforeEnter: isAuth,
                component: () => import('@/views/SqlReportsEditor'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'edit-extended-reports'
                }
            })),
            {
                path: 'system-notifications',
                name: 'systemNotifications',
                beforeEnter: isAuth,
                component: () => import('@/views/SystemNotifications'),
                meta: {
                    permission: ['business', 'leader', 'admin']
                }
            },
            {
                path: 'system-logs',
                name: 'systemLogs',
                beforeEnter: isAuth,
                component: () => import('@/views/SystemLogs'),
                meta: {
                    permission: ['admin'],
                    accessPermission: 'watch-system-logs'
                }
            },
            {
                path: 'system-logs/settings',
                name: 'systemLogsSettings',
                beforeEnter: isAuth,
                component: () => import('@/views/SystemLogs/Settings'),
                meta: {
                    permission: ['admin']
                }
            },
            {
                path: 'settings',
                name: 'settings',
                beforeEnter: isAuth,
                component: () => import('@/views/Settings'),
                meta: {
                    permission: ['admin', 'leader', 'business']
                }
            },
            {
                path: 'moderation',
                name: 'moderation',
                beforeEnter: isAuth,
                component: () => import('@/views/Moderation'),
                meta: {
                    permission: ['admin']
                }
            }
        ]
    },
    {
        path: '',
        component: ClientLayout,
        children: [
            {
                path: 'login',
                beforeEnter: isNotAuth,
                component: () => import('@/views/Login'),
                meta: {
                    permission: ['client', 'business', 'leader']
                }
            },
            {
                path: 'reset/:token',
                component: () => import('@/views/Reset')
            },
            {
                path: '/ref-register/:id',
                beforeEnter: checkClientReferralCode,
                component: () => import('@/views/RefRegister')
            },
            {
                path: '/business-register/:id',
                beforeEnter: checkBusinessReferralCode,
                component: () => import('@/views/BusinessRegister')
            },
            {
                path: '/business-register/:id/:confirmation',
                beforeEnter: checkBusinessConfirmationCode,
                component: () => import('@/views/BusinessRegister')
            },
            {
                path: '/confirmation/:confirmationCode',
                beforeEnter: isAccountConfirmed,
                component: () => import('@/views/ConfirmAccount')
            },
            { path: '*', component: () => import('@/views/ErrorPages/404'), hidden: true }
        ]
    }
]

export default new Router({
    mode: 'history',
    scrollBehavior: () => ({ y: 0 }),
    routes: constantRouterMap
})
